import { create } from 'zustand';

type ImportMeta = {
  altResId?: string;
  assetId: string;
  clipId: string;
  kind?: 'original' | 'proxy';
};

type ActiveImport = { bytes: number } & Partial<ImportMeta>;

export type BatchImportProgress = {
  /** 1-based index of the clip currently being prepared or imported. */
  current: number;
  currentFileName: string;
  total: number;
};

type ImportLaneOwner = 'batch' | 'single';

type ImportProgressState = {
  abandonedHostBatchIds: string[];
  activeHostBatchIds: string[];
  batch: BatchImportProgress | null;
  imports: Record<string, ActiveImport>;
  importLaneOwner: ImportLaneOwner | null;
  /** Clip id for the in-flight single import; used to ignore stale ImportComplete events. */
  singleLaneClipId: string | null;
  /** Host-normalized file name for the in-flight single import (set before dispatch). */
  singleLaneFileName: string | null;
  /** FIFO queue of clip ids registered at dispatch; paired with host fileName on first ImportProgress. */
  pendingImportQueue: ImportMeta[];
  abandonHostBatch: (batchId: string) => void;
  cancelPendingImport: (clipId: string) => void;
  /** Clears batch progress entries after a batch finishes or is aborted. */
  clearBatchImportProgress: () => void;
  completeImport: (fileName: string) => void;
  /** Clears progress and drops an orphaned queue head when the host reports failure without progress. */
  failImport: (fileName?: string) => void;
  /** Host reported ImportBatchComplete — drops tracking for that batch id. */
  finishHostBatch: (batchId: string) => void;
  registerImport: (meta: ImportMeta) => void;
  /** Registers a batch clip under its resolved host file name before dispatch. */
  registerPreparedImport: (meta: ImportMeta & { fileName: string }) => void;
  registerHostBatch: (batchId: string) => void;
  releaseImportLane: () => void;
  /** Releases the batch lane only when no host batches or batch prep are still in flight. */
  releaseImportLaneIfIdle: () => void;
  /** Resets all import progress state (used by Cypress component tests). */
  reset: () => void;
  setSingleLaneImport: (meta: { clipId: string; fileName: string }) => void;
  setBatchProgress: (progress: BatchImportProgress) => void;
  startBatch: (total: number) => void;
  tryAcquireImportLane: (owner: ImportLaneOwner) => boolean;
  updateProgress: (fileName: string, bytes: number) => void;
};

export const useImportProgressStore = create<ImportProgressState>((set) => ({
  abandonedHostBatchIds: [],
  activeHostBatchIds: [],
  abandonHostBatch: (batchId) =>
    set((state) => {
      const alreadyAbandoned = state.abandonedHostBatchIds.includes(batchId);
      const stillActive = state.activeHostBatchIds.includes(batchId);

      if (alreadyAbandoned && !stillActive) {
        return state;
      }

      return {
        abandonedHostBatchIds: alreadyAbandoned
          ? state.abandonedHostBatchIds
          : [...state.abandonedHostBatchIds, batchId],
        activeHostBatchIds: state.activeHostBatchIds.filter((id) => id !== batchId),
      };
    }),
  batch: null,
  cancelPendingImport: (clipId) =>
    set((state) => {
      const index = state.pendingImportQueue.findIndex((entry) => entry.clipId === clipId);

      if (index === -1) {
        return state;
      }

      const pendingImportQueue = [...state.pendingImportQueue];
      pendingImportQueue.splice(index, 1);

      return { pendingImportQueue };
    }),
  clearBatchImportProgress: () => set({ batch: null, imports: {}, pendingImportQueue: [] }),
  completeImport: (fileName) =>
    set((state) => {
      const entry = state.imports[fileName];
      const { [fileName]: _, ...imports } = state.imports;
      const pendingImportQueue =
        entry?.clipId != null
          ? state.pendingImportQueue.filter((item) => item.clipId !== entry.clipId)
          : state.pendingImportQueue;

      return { imports, pendingImportQueue };
    }),
  failImport: (fileName) =>
    set((state) => {
      if (fileName != null && fileName in state.imports) {
        const entry = state.imports[fileName];
        const { [fileName]: _, ...imports } = state.imports;
        const pendingImportQueue =
          entry?.clipId != null
            ? state.pendingImportQueue.filter((item) => item.clipId !== entry.clipId)
            : state.pendingImportQueue;

        return { imports, pendingImportQueue };
      }

      if (state.pendingImportQueue.length === 0) {
        return state;
      }

      return {
        imports: state.imports,
        pendingImportQueue: state.pendingImportQueue.slice(1),
      };
    }),
  finishHostBatch: (batchId) =>
    set((state) => ({
      abandonedHostBatchIds: state.abandonedHostBatchIds.filter((id) => id !== batchId),
      activeHostBatchIds: state.activeHostBatchIds.filter((id) => id !== batchId),
    })),
  importLaneOwner: null,
  imports: {},
  pendingImportQueue: [],
  singleLaneClipId: null,
  singleLaneFileName: null,
  registerImport: (meta) =>
    set((state) => ({
      pendingImportQueue: [...state.pendingImportQueue, meta],
    })),
  registerPreparedImport: ({ assetId, clipId, fileName }) =>
    set((state) => ({
      imports: {
        ...state.imports,
        [fileName]: { assetId, bytes: 0, clipId },
      },
    })),
  registerHostBatch: (batchId) =>
    set((state) => {
      if (state.activeHostBatchIds.includes(batchId)) {
        return state;
      }

      return { activeHostBatchIds: [...state.activeHostBatchIds, batchId] };
    }),
  releaseImportLane: () => set({ importLaneOwner: null, singleLaneClipId: null, singleLaneFileName: null }),
  releaseImportLaneIfIdle: () =>
    set((state) => {
      if (state.activeHostBatchIds.length > 0 || state.batch != null) {
        return state;
      }

      if (state.importLaneOwner !== 'batch') {
        return state;
      }

      return { importLaneOwner: null, singleLaneClipId: null, singleLaneFileName: null };
    }),
  reset: () =>
    set({
      abandonedHostBatchIds: [],
      activeHostBatchIds: [],
      batch: null,
      importLaneOwner: null,
      imports: {},
      pendingImportQueue: [],
      singleLaneClipId: null,
      singleLaneFileName: null,
    }),
  setSingleLaneImport: ({ clipId, fileName }) => set({ singleLaneClipId: clipId, singleLaneFileName: fileName }),
  setBatchProgress: (progress) => set({ batch: progress }),
  startBatch: (total) =>
    set({
      batch: {
        current: 1,
        currentFileName: '',
        total,
      },
    }),
  tryAcquireImportLane: (owner) => {
    let acquired = false;

    set((state) => {
      if (state.importLaneOwner != null || state.activeHostBatchIds.length > 0 || state.batch != null) {
        return state;
      }

      acquired = true;

      return { importLaneOwner: owner, singleLaneClipId: null, singleLaneFileName: null };
    });

    return acquired;
  },
  updateProgress: (fileName, bytes) =>
    set((state) => {
      const existing = state.imports[fileName];
      const hasMeta = existing?.clipId != null;
      const [nextPending, ...restQueue] = state.pendingImportQueue;
      const meta = hasMeta ? undefined : nextPending;

      return {
        imports: {
          ...state.imports,
          [fileName]: { ...existing, ...meta, bytes },
        },
        pendingImportQueue: hasMeta ? state.pendingImportQueue : restQueue,
      };
    }),
}));
